import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { groundZone, centerpiece, pulseField } from './zoneBuilders';

// =============================================================================
// SHAMAN VFX — totem zone defs (zone-def re-categorization batch)
// =============================================================================
//
// First registered defs for the three totems — all three vfxIds were
// unregistered (legacy-particle fallback) before this batch. Each is a
// category:'zone' def under the wave-4 lifetime contract: world-anchored
// (never a followTarget), looping legal ONLY because the def-level `duration`
// ceiling + the tracked-persistent stop path bound it (PF-ZONE-NO-DURATION).
//
// Durations are the gameplay durations (TOML/ability data):
//   searing_totem 10000 · healing_totem 8000 · windfury_totem 12000
//
// Healing Stream and Searing use AuraEffectBehavior's world anchor: one fixed
// cast-point instance with an explicit expiry. Windfury is core-only and still
// uses the older caster-following self-buff path; its party-area mechanic and
// zone expectation remain separately reported debt.
// =============================================================================

/**
 * Searing Totem — planted fire totem scorching the area.
 * Pole + ember field; pulse synced to the 1200ms damage tick.
 */
const SEARING_TOTEM: VFXEffectConfig = {
  id: 'searing_totem',
  name: 'Searing Totem',
  category: 'zone',
  description: 'Planted fire totem scorching nearby enemies',
  duration: 10000,
  primitives: [
    ...groundZone({
      ring: { width: 150, fill: 0xFF5500, rim: 0xFFAA33, alpha: 0.16, pulse: { alpha: 0.28, duration: 1200 } },
      fall: { direction: 'up', count: 6, spread: 60, color: 0xFFCC44, alpha: 0.7, travel: 34, periodMs: 900, glow: true },
      pulse: { radius: 30, color: 0xFF7722, alpha: 0.35, periodMs: 1200, expandTo: 2.2, glow: true },
    }),
    // The totem itself — carved pole with a flickering ember crown
    centerpiece({
      shape: 'rect', width: 12, height: 46,
      color: 0x8B3A1A, accent: 0xFF6600, alpha: 0.95,
      flicker: { alpha: 0.75, duration: 600 },
    }),
    centerpiece({
      shape: 'star', width: 10,
      color: 0xFFAA33, accent: 0xFFEE88, glow: true, depth: 15,
      offsetY: -34,
      flicker: { alpha: 0.5, scale: 1.25, duration: 400 },
    }),
  ],
};

/**
 * Healing Stream Totem — planted water totem, soothing tide field.
 * Pulse synced to the 1000ms heal tick; rising droplet motes.
 */
const HEALING_STREAM_TOTEM: VFXEffectConfig = {
  id: 'healing_totem',
  name: 'Healing Stream Totem',
  category: 'zone',
  description: 'Planted water totem restoring nearby allies',
  duration: 8000,
  primitives: [
    ...groundZone({
      ring: { width: 140, fill: 0x33CCAA, rim: 0x99FFE6, alpha: 0.16, pulse: { alpha: 0.26, duration: 1000 } },
      fall: { direction: 'up', count: 5, spread: 55, color: 0xAAFFEE, alpha: 0.75, travel: 30, periodMs: 1000, glow: true },
      pulse: { radius: 28, color: 0x66E6C8, alpha: 0.3, periodMs: 1000, expandTo: 2.0 },
    }),
    centerpiece({
      shape: 'rect', width: 12, height: 44,
      color: 0x2E6E5E, accent: 0x66FFDD, alpha: 0.95,
      flicker: { alpha: 0.8, duration: 1000 },
    }),
    centerpiece({
      shape: 'star', width: 9,
      color: 0x99FFE6, accent: 0xE6FFFA, glow: true, depth: 15,
      offsetY: -32,
      flicker: { alpha: 0.55, scale: 1.2, duration: 800 },
    }),
  ],
};

/**
 * Windfury Totem — planted air totem, restless gust perimeter.
 */
const WINDFURY_TOTEM: VFXEffectConfig = {
  id: 'windfury_totem',
  name: 'Windfury Totem',
  category: 'zone',
  description: 'Planted air totem empowering nearby allies with wind',
  duration: 12000,
  primitives: [
    ...groundZone({
      ring: { width: 150, fill: 0x00B3A4, rim: 0x9FF2E8, alpha: 0.14, pulse: { alpha: 0.24, duration: 900 } },
      edge: { count: 4, rimRadius: 70, shape: 'circle', size: 4, color: 0xE6FFFF, alpha: 0.8, speed: 0.5, glow: true },
    }),
    centerpiece({
      shape: 'rect', width: 12, height: 46,
      color: 0x274E5E, accent: 0x00B3A4, alpha: 0.95,
      sway: { rotation: 0.06, duration: 700 },
    }),
    // Gust whorl at the totem base — fast expanding pulse reads as wind
    pulseField({ radius: 16, color: 0xCFFFF6, alpha: 0.45, depth: 15, periodMs: 700, expandTo: 1.9, glow: true }),
  ],
};

export const SHAMAN_VFX: VFXEffectConfig[] = [
  SEARING_TOTEM,
  HEALING_STREAM_TOTEM,
  WINDFURY_TOTEM,
];
